import * as Sentry from '@sentry/browser';
import { HttpStatusCodes } from '@zocdoc-frontend-common/utils-constants';
import { fetchWithDatadog } from '@zocdoc-frontend-common/utils-react';
import { BASE_EXPIRED_PASSWORD_REDIRECT } from '../constants';

const apiHost =
    process.env.JEST_API_HOST || process.env.STORYBOOK_SIGN_IN_API_HOST || '';

/**
 * @description sends verification code to authenticate phone
 * @property {string} email
 * @property {string} verificationId
 * @property {string} verificationToken
 * @property {string} verificationCode
 * @property {string} application - application name for datadog metrics, defaults to frontend-common
 * @returns {AUTHENTICATE_PHONE|null} authenticatePhone
 */

export async function authenticatePhone({
    email,
    verificationId,
    verificationToken,
    verificationCode,
    application,
}) {
    const endpoint = `${apiHost}/accounts/v2/authentication/authenticate-phone`;

    const body = {
        username: email,
        verification_id: verificationId,
        verification_token: verificationToken,
        verification_code: verificationCode,
    };

    const response = await fetchWithDatadog({
        body: JSON.stringify(body),
        endpoint,
        method: 'POST',
        operationId: 'authenticate_phone_mfa_post',
        pageName: 'sign-in',
        application,
    });

    if (response?.status === HttpStatusCodes.OK) {
        const jsonResponse = await response.json();

        return {
            success: true,
            body: jsonResponse,
        };
    }

    if (response?.status === HttpStatusCodes.AuthenticationTimeout) {
        const jsonResponse = await response.json();
        return {
            success: false,
            body: {
                redirectUrl:
                    jsonResponse?.redirect_url ||
                    BASE_EXPIRED_PASSWORD_REDIRECT,
            },
        };
    }

    if (response?.status === HttpStatusCodes.BadRequest) {
        const jsonResponse = await response.json();

        if (jsonResponse?.redirect_url) {
            return {
                body: {
                    redirectUrl: jsonResponse.redirect_url,
                },
                success: false,
                errorCode: jsonResponse.error_code ?? '',
            };
        }

        return {
            success: false,
            errorMessage:
                jsonResponse.error_message ??
                'There was a problem logging you in.',
            errorCode: jsonResponse.error_code ?? '',
        };
    }

    if (response?.status === HttpStatusCodes.Conflict) {
        const jsonResponse = await response.json();

        return {
            success: false,
            errorMessage:
                jsonResponse?.error_message ??
                'There was a problem logging you in.',
        };
    }

    if (
        response?.status === HttpStatusCodes.Unauthorized ||
        response?.status === HttpStatusCodes.InternalServerError ||
        response?.status === 501
    ) {
        return {
            success: false,
            errorMessage: 'There was a problem logging you in.',
        };
    }

    Sentry.withScope((scope) => {
        scope.setExtras({ statusText: response?.statusText });
        scope.setTag('status_code', response?.status);
        Sentry.captureException(
            Error('Failed to authenticate user with MFA verification code')
        );
    });

    return null;
}
